<template>
  <div>
    <LegalDocumentsEmptyState v-if="!hasDocuments" :document-types />
    <div v-if="hasDocuments" class="mt-5">
      <div
        v-for="formattedDocument in formattedDocuments"
        :key="formattedDocument.documentType.code"
        class="mb-20 mt-32"
      >
        <div class="body-1-bold mb-20 flex items-center">
          {{ formattedDocument.documentType.label }}
          <RevTooltip v-if="formattedDocument.tooltipContent" class="ml-8">
            <template #trigger="{ hide, show }">
              <IconInfo
                variant="secondary"
                @focusin="show"
                @focusout="hide"
                @mouseleave="hide"
                @mouseover="show"
              />
            </template>

            {{ formattedDocument.tooltipContent }}
          </RevTooltip>
        </div>
        <div v-if="formattedDocument.documents.length">
          <div
            v-for="document in formattedDocument.documents"
            :key="document.id"
          >
            <div
              v-if="document.id"
              class="body-1 flex items-center justify-between md:w-[500px]"
            >
              <div class="mr-12 truncate">
                {{ document.filename }}
              </div>
              <div class="flex shrink gap-8">
                <RevButtonIcon
                  v-if="document.link?.href"
                  :aria-label="i18n(translations.downloadButton)"
                  :icon="IconDownload"
                  target="_blank"
                  :to="document.link.href"
                  variant="secondary"
                />
                <RevButtonIcon
                  :aria-label="i18n(translations.deleteButton)"
                  :icon="IconTrash"
                  variant="secondary"
                  @click="handleDeleteDocument(document)"
                />
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="!formattedDocument.documents.length"
          class="flex items-center"
        >
          <IconWarning class="fill-danger mr-12" />
          <div class="body-1">
            {{ i18n(translations.emptyLegalDocument) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type {
  GetLegalDocuments,
  GetLegalDocumentsItem,
  GetLegalDocumentsMetadata,
  GetLegalDocumentsMetadataItem,
} from '@backmarket/http-api/src/api-specs-seller-backoffice/seller-profile'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconDownload } from '@ds/icons/IconDownload'
import { IconInfo } from '@ds/icons/IconInfo'
import { IconTrash } from '@ds/icons/IconTrash'
import { IconWarning } from '@ds/icons/IconWarning'

import { deleteLegalDocument } from '../../../../../../Profile.utils'
import { getDocumentDescription } from '../../LegalDocuments.utils'
import LegalDocumentsEmptyState from '../LegalDocumentsEmptyState/LegalDocumentsEmptyState.vue'

import translations from './LegalDocumentsList.translations'
import type { ExtendedLegalDocument } from './LegalDocumentsList.types'

const props = defineProps<{
  documentTypes: GetLegalDocumentsMetadata['legalDocumentTypes']
  legalDocuments: GetLegalDocuments
}>()

const i18n = useI18n()
const { openSuccessToast, openErrorToast } = useTheToast()

const emit = defineEmits(['delete-legal-document'])

const hasDocuments = computed(() => !!props.legalDocuments.length)

function getDocuments(documentType: GetLegalDocumentsMetadataItem) {
  return props.legalDocuments.reduce((documents, document) => {
    if (document.type.code === documentType.code) {
      documents.push(document)
    }

    return documents
  }, [] as GetLegalDocuments)
}

const formattedDocuments = computed<ExtendedLegalDocument[]>(() =>
  props.documentTypes.map((documentType) => ({
    tooltipContent: getDocumentDescription(i18n, documentType.code),
    documentType,
    documents: getDocuments(documentType),
  })),
)

async function handleDeleteDocument({ id }: GetLegalDocumentsItem) {
  try {
    await deleteLegalDocument(id)
    emit('delete-legal-document')
    openSuccessToast({ title: i18n(translations.toastDeleteSuccessContent) })
  } catch {
    openErrorToast({ title: i18n(translations.toastErrorContent) })
  }
}
</script>
